import { Dictionary } from 'lodash';
import camelCase from 'lodash-es/camelCase';
import mapKeys from 'lodash-es/mapKeys';
import {
    users_api_please_call_me_url,
    users_api_send_sms_token_url,
    users_api_verify_sms_token_url,
} from 'mk/urls/functions';
import { toastAdd } from 'mk2/actions';
import { SIGNUP_FORM_NAME } from 'mk2/apps/users/containers/Signup/Signup.constants';
import { cleanPhoneNumber } from 'mk2/apps/users/utils';
import ServerErrorToast from 'mk2/components/toasts/ServerErrorToast';
import { FormPageType } from 'mk2/constants';
import {
    PHONE_VERIFICATION_REQUEST_CALL,
    PHONE_VERIFICATION_REQUEST_TOKEN,
    PHONE_VERIFICATION_VERIFY_TOKEN,
    phoneVerificationGoTo,
    PhoneVerificationRequestCallAction,
    PhoneVerificationRequestTokenAction,
    phoneVerificationSaveCallMe,
    phoneVerificationSaveToken,
    phoneVerificationSendSms,
    PhoneVerificationVerifyTokenAction,
} from 'mk2/containers/PhoneVerification/PhoneVerification.actions';
import { PHONE_FORM_NAME, TOKEN_FORM_NAME } from 'mk2/containers/PhoneVerification/PhoneVerification.constants';
import { handleXHRPostErrorSaga, normalizeError, XHRAction, XHRFormError, XHRPostError } from 'mk2/helpers/api';
import { handleXHRFormErrorSaga } from 'mk2/helpers/form';
import { mkReduxFormOpenPage } from 'mk2/helpers/form.actions';
import { getLogger } from 'mk2/logger';
import { reset, stopSubmit } from 'redux-form';
import { all, call, put, takeLatest } from 'redux-saga/effects';

const logger = getLogger('PhoneVerification.sagas');

function* requestToken({ prefix, phoneNumber, mode, xhr }: PhoneVerificationRequestTokenAction & XHRAction) {
    yield put(phoneVerificationSendSms.request(prefix, phoneNumber, mode));

    try {
        const response = yield call(() => xhr.post(users_api_send_sms_token_url(), {
            prefix,
            phone_number: phoneNumber,
            mode,
        }));

        yield put(phoneVerificationSendSms.success(prefix, phoneNumber, mode, {
            prefix: response.body.prefix,
            phoneNumber: response.body.phone_number,
        }));

        // Stop submit
        yield put(stopSubmit(PHONE_FORM_NAME));

        // Reset form
        yield put(reset(PHONE_FORM_NAME));

        // Go to next step
        if (mode === 'signup-no-user') {
            yield put(mkReduxFormOpenPage(SIGNUP_FORM_NAME, FormPageType.SignupTokenValidation));
        } else {
            yield put(phoneVerificationGoTo(2));
        }
    } catch (error) {
        if (error.status === 400) {
            const errors = mapKeys(error.response.body.fieldErrors, (value: string[], key: string, collection: Dictionary<string[]>) => camelCase(key));
            yield put(stopSubmit(PHONE_FORM_NAME, errors));
            if (mode === 'signup-no-user') {
                yield handleXHRFormErrorSaga(SIGNUP_FORM_NAME, error as XHRFormError, logger);
            }
            yield put(phoneVerificationSendSms.failure(prefix, phoneNumber, mode, !!error.response.body.limitReached, normalizeError(error)));
        } else {
            logger.error(error);

            yield put(stopSubmit(PHONE_FORM_NAME));

            // Display toast message
            yield put(toastAdd('error', {
                component: ServerErrorToast,
            }));

            yield put(phoneVerificationSendSms.failure(prefix, phoneNumber, mode, false, normalizeError(error)));
        }
    }
}

function* verifyToken({ token, mode, fullPhoneNumber, xhr }: PhoneVerificationVerifyTokenAction & XHRAction) {
    yield put(phoneVerificationSaveToken.request(token, mode));

    try {
        const payload: any = {
            sms_code: token,
            mode,
        };

        if (mode === 'strollers' || mode === 'signup-no-user') {
            payload.phone_number = fullPhoneNumber;
        }

        const response = yield call(() => xhr.post(users_api_verify_sms_token_url(), payload));

        yield put(phoneVerificationSaveToken.success(token, mode));

        // Stop submit
        yield put(stopSubmit(TOKEN_FORM_NAME));

        // Reset form
        yield put(reset(TOKEN_FORM_NAME));

        // Go to next step
        if (mode === 'signup-no-user') {
            yield put(mkReduxFormOpenPage(SIGNUP_FORM_NAME, FormPageType.SignupPhotoUpload));
        }
    } catch (error) {
        if (error.status === 400) {
            yield put(stopSubmit(TOKEN_FORM_NAME, {
                token: error.response.body.fieldErrors.sms_code,
            } as any));

            yield put(phoneVerificationSaveToken.failure(token, mode, normalizeError(error)));
        } else {
            logger.error(error);

            yield put(stopSubmit(TOKEN_FORM_NAME));

            // Display toast message
            yield put(toastAdd('error', {
                component: ServerErrorToast,
            }));

            yield put(phoneVerificationSaveToken.failure(token, mode, normalizeError(error)));
        }
    }
}

function* requestCall({ prefix, phoneNumber, mode, xhr }: PhoneVerificationRequestCallAction & XHRAction) {
    yield put(phoneVerificationSaveCallMe.request(prefix, phoneNumber, mode));

    try {
        // Go to next step
        yield put(phoneVerificationGoTo(3));

        const response = yield call(() => xhr.post(users_api_please_call_me_url(), {
            prefix,
            phone_number: phoneNumber,
            mode,
        }));

        yield put(phoneVerificationSaveCallMe.success(prefix, phoneNumber, mode, {
            prefix: response.body.prefix,
            phoneNumber: response.body.phone_number,
        }));

    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, logger);
        yield put(phoneVerificationSaveCallMe.failure(prefix, phoneNumber, mode, normalizeError(error)));
    }
}

export default function* root() {
    yield all([
        takeLatest(PHONE_VERIFICATION_REQUEST_TOKEN, requestToken),
        takeLatest(PHONE_VERIFICATION_REQUEST_CALL, requestCall),
        takeLatest(PHONE_VERIFICATION_VERIFY_TOKEN, verifyToken),
    ]);
}
